import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import Layout from './components/Layout.jsx';
import { Spinner } from './components/ui.jsx';
import { AuthProvider } from './lib/AuthContext.jsx';
import Home from './pages/Home.jsx';
import ProductDetail from './pages/ProductDetail.jsx';
import HowItWorks from './pages/HowItWorks.jsx';
import NotFound from './pages/NotFound.jsx';

/**
 * Route table.
 *
 * The admin area is lazily loaded as its own chunk. It is a small fraction of
 * traffic but carries the Firebase Auth SDK and the form-heavy screens, so
 * splitting it keeps that weight off the storefront a shopper actually loads.
 */

const AdminLayout = lazy(() => import('./pages/admin/AdminLayout.jsx'));
const AdminLogin = lazy(() => import('./pages/admin/Login.jsx'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard.jsx'));
const AdminProductList = lazy(() => import('./pages/admin/ProductList.jsx'));
const AdminProductForm = lazy(() => import('./pages/admin/ProductForm.jsx'));

function RouteFallback() {
  return (
    <div className="grid min-h-[60vh] place-items-center" role="status" aria-live="polite">
      <span className="inline-flex items-center gap-3 text-sm font-medium text-ink-500">
        <Spinner className="size-5" />
        Loading…
      </span>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      {/* ── Storefront ─────────────────────────────────────────────────────── */}
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="how-it-works" element={<HowItWorks />} />

        {/* Each product gets its own shareable, bookmarkable URL. */}
        <Route path="products/:slug" element={<ProductDetail />} />
        {/* Bare /products has no page of its own; the catalogue lives at /. */}
        <Route path="products" element={<Navigate to="/" replace />} />

        <Route path="*" element={<NotFound />} />
      </Route>

      {/* ── Admin ──────────────────────────────────────────────────────────── */}
      <Route
        path="/admin"
        element={
          <AuthProvider>
            <Suspense fallback={<RouteFallback />}>
              <AdminLayout />
            </Suspense>
          </AuthProvider>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="login" element={<AdminLogin />} />
        <Route path="products" element={<AdminProductList />} />
        <Route path="products/new" element={<AdminProductForm mode="create" />} />
        <Route path="products/:id/edit" element={<AdminProductForm mode="edit" />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>
    </Routes>
  );
}
